import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../adminDataProvider';

type KickMemberParams = {
  userId: string;
  channelUrl: string;
};

export const useKickMember = (
  options?: UseMutationOptions<void, Error, KickMemberParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kick-member'],
    mutationFn: ({ userId, channelUrl }: KickMemberParams) =>
      dataProvider.postKickMember(channelUrl, userId),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        // Invalidate all channel member queries so lists refresh after a kick
        queryClient.invalidateQueries({
          queryKey: ['channel-members'],
        }),
        // Also invalidate operator queries if operators are displayed
        queryClient.invalidateQueries({
          queryKey: ['channel-operators'],
        }),
      ]);

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: async (error, variables, onMutateResult, context) => {
      notify(translate('chat.kickMemberError'), {
        autoHideDuration: 5000,
      });
      await options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};
